import type { ReactNode } from "react"

import { PAIS_POR_DEFECTO, PAISES } from "@/lib/paises"

import type { Empresa } from "./tipos"

/**
 * Los campos de la configuración, agrupados por para qué sirven.
 *
 * ⚠ **El agrupamiento no es cosmético: dice dónde sale cada dato.** «Identidad» va al
 * membrete de todo lo que se imprime; «Legal y parafiscal» sale en contratos y
 * planillas; «Declaraciones» encabeza el manual. Un formulario plano de veinte campos
 * obliga a saberlo de memoria, y entonces nadie sabe qué rompe al cambiar uno.
 */

export type Campos = Partial<Empresa>

const ESTILO = {
  borderColor: "var(--border)",
  backgroundColor: "var(--surface)",
  color: "var(--fg)",
}

export function Grupo({
  titulo,
  descripcion,
  children,
}: {
  titulo: string
  descripcion: string
  children: ReactNode
}) {
  return (
    <section className="superficie p-4">
      <h2 className="text-base font-semibold">{titulo}</h2>
      <p className="mb-4 text-sm" style={{ color: "var(--fg-muted)" }}>
        {descripcion}
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </section>
  )
}

export function Campo({
  id,
  etiqueta,
  valor,
  alCambiar,
  ancho,
  ayuda,
  numerico,
  placeholder,
  maxLength,
  soloLectura,
}: {
  id: string
  etiqueta: string
  valor: string
  alCambiar: (v: string) => void
  /** `completo` ocupa las dos columnas. */
  ancho?: "completo"
  ayuda?: string
  numerico?: boolean
  placeholder?: string
  maxLength?: number
  soloLectura?: boolean
}) {
  return (
    <div className={ancho === "completo" ? "sm:col-span-2" : undefined}>
      <label htmlFor={id} className="mb-1 block text-xs font-medium uppercase">
        {etiqueta}
      </label>
      <input
        id={id}
        value={valor}
        onChange={(e) => alCambiar(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        readOnly={soloLectura}
        className={`h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm ${
          numerico ? "tabular" : ""
        } ${soloLectura ? "opacity-60" : ""}`}
        style={ESTILO}
      />
      {ayuda && (
        <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
          {ayuda}
        </p>
      )}
    </div>
  )
}

export function Area({
  id,
  etiqueta,
  valor,
  alCambiar,
  filas = 3,
  ayuda,
  soloLectura,
}: {
  id: string
  etiqueta: string
  valor: string
  alCambiar: (v: string) => void
  filas?: number
  ayuda?: string
  soloLectura?: boolean
}) {
  return (
    <div className="sm:col-span-2">
      <label htmlFor={id} className="mb-1 block text-xs font-medium uppercase">
        {etiqueta}
      </label>
      <textarea
        id={id}
        value={valor}
        onChange={(e) => alCambiar(e.target.value)}
        rows={filas}
        readOnly={soloLectura}
        className={`w-full rounded-[var(--radius-control)] border px-2 py-1.5 text-sm ${
          soloLectura ? "opacity-60" : ""
        }`}
        style={ESTILO}
      />
      {ayuda && (
        <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
          {ayuda}
        </p>
      )}
    </div>
  )
}

export function SelectorPais({
  valor,
  alCambiar,
  soloLectura,
}: {
  valor: string
  alCambiar: (v: string) => void
  soloLectura?: boolean
}) {
  return (
    <div>
      <label htmlFor="e-pais" className="mb-1 block text-xs font-medium uppercase">
        País del teléfono
      </label>
      <select
        id="e-pais"
        value={valor || PAIS_POR_DEFECTO}
        onChange={(e) => alCambiar(e.target.value)}
        disabled={soloLectura}
        className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm disabled:opacity-60"
        style={ESTILO}
      >
        {PAISES.map((p) => (
          <option key={p.iso} value={p.iso}>
            {p.nombre} ({p.prefijo})
          </option>
        ))}
      </select>
    </div>
  )
}
